import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import JsonLd from "@/components/feature/JsonLd";
import PageSeo from "@/components/feature/PageSeo";
import GossipCard from "@/components/feature/GossipCard";
import { getItemById, getRelatedItems, ranking, siteInfo } from "@/mocks/gossip";

export default function ArticlePage() {
  const { id = "" } = useParams<{ id: string }>();
  const item = getItemById(id);
  const [activeImage, setActiveImage] = useState(0);

  if (!item) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 w-full flex flex-col items-center justify-center text-center px-4 py-24">
          <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-3xl text-foreground-500">
            <i className="ri-emotion-sad-line" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这条瓜料已经找不到了</h1>
          <p className="mt-2 text-sm text-foreground-600">它可能已下架或链接有误，去首页看看最新更新的热瓜吧。</p>
          <Link
            to="/"
            className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelatedItems(item.id, 3);
  const topRanking = ranking.slice(0, 5);

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: item.title,
    description: item.summary,
    datePublished: item.date,
    dateModified: item.date,
    inLanguage: "zh-CN",
    author: { "@type": "Organization", name: item.author },
    publisher: { "@type": "Organization", name: siteInfo.brand },
    articleSection: item.category,
    keywords: item.tags.join(","),
    image: item.images,
    mainEntityOfPage: `https://hebeishifenhao.cn/article/${item.id}`,
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://hebeishifenhao.cn/" },
      {
        "@type": "ListItem",
        position: 2,
        name: item.category,
        item: `https://hebeishifenhao.cn/category/${item.categorySlug}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: item.title,
        item: `https://hebeishifenhao.cn/article/${item.id}`,
      },
    ],
  };

  const typeLabel = item.type === "video" ? "视频流出" : item.type === "image" ? "图集实拍" : "速览整理";

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <PageSeo
        title={`${item.title} - 网曝吃瓜画面 | 瓜田前线`}
        description={item.summary}
        keywords={`${item.tags.join(",")},网曝吃瓜,吃瓜画面,${item.category}`}
        canonical={`https://hebeishifenhao.cn/article/${item.id}`}
      />
      <JsonLd data={articleLd} />
      <JsonLd data={breadcrumbLd} />
      <Navbar />

      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to="/category" className="hover:text-primary-600 transition-colors">
              全部瓜料
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to={`/category/${item.categorySlug}`} className="hover:text-primary-600 transition-colors">
              {item.category}
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-800 line-clamp-1">{item.title}</span>
          </nav>
        </div>

        <div className="w-full px-4 md:px-8 pb-14 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <article className="lg:col-span-8">
              <header>
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-label">
                    <i className="ri-fire-fill" aria-hidden="true"></i>
                    {item.category}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label">
                    <i className="ri-record-circle-line" aria-hidden="true"></i>
                    {typeLabel}
                  </span>
                </div>
                <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
                  {item.title}
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">{item.summary}</p>
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 border-b border-background-200 pb-5">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-user-3-line" aria-hidden="true"></i>
                    {item.author}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-calendar-line" aria-hidden="true"></i>
                    {item.date}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-eye-line" aria-hidden="true"></i>
                    {item.views} 浏览
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-chat-3-line" aria-hidden="true"></i>
                    {item.comments} 讨论
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-primary-600">
                    <i className="ri-fire-line" aria-hidden="true"></i>
                    热度 {item.heat}
                  </span>
                </div>
              </header>

              <figure className="mt-6">
                <div className="w-full aspect-[16/10] rounded-lg overflow-hidden bg-background-200 border border-background-200">
                  <img
                    src={item.images[activeImage]}
                    alt={`${item.title} 第 ${activeImage + 1} 张画面`}
                    title={`${item.title} - 网曝吃瓜实拍画面`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <figcaption className="mt-2 text-xs text-foreground-500 text-center">
                  图 {activeImage + 1} / {item.images.length} · 画面整理自网络公开信息
                </figcaption>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {item.images.map((img, index) => (
                    <button
                      key={img}
                      type="button"
                      onClick={() => setActiveImage(index)}
                      aria-label={`查看第 ${index + 1} 张画面`}
                      className={`relative aspect-[4/3] rounded-md overflow-hidden border-2 transition-colors cursor-pointer ${
                        activeImage === index ? "border-primary-500" : "border-background-200 hover:border-background-400"
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${item.title} 缩略图 ${index + 1}`}
                        title={`${item.title} - 画面缩略图`}
                        className="w-full h-full object-cover object-top"
                      />
                    </button>
                  ))}
                </div>
              </figure>

              <div className="mt-8 space-y-5">
                {item.content.map((paragraph, index) => (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-loose">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 pt-6 border-t border-background-200">
                <h2 className="font-heading text-base font-semibold text-foreground-900">相关标签</h2>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {item.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category?q=${encodeURIComponent(tag)}`}
                      className="px-3.5 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                    >
                      # {tag}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-8 p-5 rounded-lg bg-accent-100 border border-accent-200 flex items-start gap-4">
                <span className="w-10 h-10 shrink-0 rounded-md bg-accent-500 text-foreground-950 flex items-center justify-center text-xl">
                  <i className="ri-lightbulb-line" aria-hidden="true"></i>
                </span>
                <div>
                  <h2 className="font-heading text-sm font-semibold text-foreground-900">理性吃瓜提示</h2>
                  <p className="mt-1.5 text-sm text-foreground-700 leading-relaxed">
                    网络流传的画面可能存在剪辑或断章取义。建议结合多方信息判断，不传播未经证实的内容，尊重当事人的合法权益。
                  </p>
                </div>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-6">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-base font-semibold text-foreground-900 flex items-center gap-2">
                    <i className="ri-bar-chart-2-line text-primary-600" aria-hidden="true"></i>
                    本条数据
                  </h2>
                  <dl className="mt-4 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <dt className="text-foreground-500">综合热度</dt>
                      <dd className="font-heading font-bold text-primary-600">{item.heat}</dd>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <dt className="text-foreground-500">浏览量</dt>
                      <dd className="font-medium text-foreground-900">{item.views}</dd>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <dt className="text-foreground-500">讨论数</dt>
                      <dd className="font-medium text-foreground-900">{item.comments}</dd>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <dt className="text-foreground-500">发布时间</dt>
                      <dd className="font-medium text-foreground-900">{item.date}</dd>
                    </div>
                  </dl>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-base font-semibold text-foreground-900 flex items-center gap-2">
                    <i className="ri-fire-line text-primary-600" aria-hidden="true"></i>
                    热瓜榜 TOP5
                  </h2>
                  <ol className="mt-4 space-y-3">
                    {topRanking.map((rank, index) => (
                      <li key={rank.id} className="flex items-start gap-3">
                        <span
                          className={`w-6 h-6 shrink-0 rounded flex items-center justify-center text-xs font-label ${
                            index < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <Link
                          to={`/article/${rank.id}`}
                          className="text-sm text-foreground-700 leading-snug hover:text-primary-600 transition-colors line-clamp-2"
                        >
                          {rank.title}
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="rounded-lg bg-primary-950 text-background-100 p-5">
                  <h2 className="font-heading text-base font-semibold text-background-50">有新的现场画面？</h2>
                  <p className="mt-2 text-sm text-background-300 leading-relaxed">
                    欢迎投稿线索，注明时间与地点，我们会先核验再收录。
                  </p>
                  <a
                    href="#subscribe"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent-300 hover:text-accent-200 transition-colors"
                  >
                    前往订阅
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </a>
                </div>
              </div>
            </aside>
          </div>

          <section className="mt-14 pt-10 border-t border-background-200">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">相关瓜料推荐</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((rel) => (
                <GossipCard key={rel.id} item={rel} />
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}